// WordPress dependencies
import {
    PanelBody,
    PanelRow,
    SelectControl,
    TextControl,
    RadioControl,
    RangeControl,
    __experimentalNumberControl as NumberControl,
} from "@wordpress/components";
import { __ } from "@wordpress/i18n";
import { useState } from "react";

// Internal dependencies
import { ucFirst } from "../../utils";
import MediaUploader from "../elements/media-uploader";
import IndicatorImageContentTab from "./IndicatorImageContentTab";

const coreAssets = window.COCA_BAIS_ASSETS;

export default (props) => {
    const isTripleEnabled = !!props.proSettings.is_triple_active && !!props.proSettings.is_pro_active;
    const sliderType = props.attributes.slider_type;
    const isTriple = isTripleEnabled && sliderType === "triple";
    const isVideo = sliderType === "video";
    const isRetouch = sliderType === "retouch";
    // Use a single videoType state that will control both before and after
    // Initialize videoType from saved attributes, with fallback to 'youtube' only if no value is set
    // We prioritize 'upload' type if either before or after is set to upload
    const getInitialVideoType = () => {
        // if (props.attributes.video_before_type == "upload" || props.attributes.video_after_type == "upload") {
        //     return "upload";
        // }
        return props.attributes.video_source_type; // Default to 'youtube' if no upload type is set
    };
    const [videoType, setVideoType] = useState(getInitialVideoType());
    const [beforeYoutubeError, setBeforeYoutubeError] = useState("");
    const [afterYoutubeError, setAfterYoutubeError] = useState("");
    let tabTitle = __("Before & After Images", "wp-before-after-image-slider");

    // YouTube URL validation function
    const validateYoutubeUrl = (url) => {
        if (!url) return true; // Empty is valid (not required)

        // Match standard YouTube URLs (youtube.com and youtu.be)
        const youtubeRegex =
            /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})(\S*)?$/;
        return youtubeRegex.test(url);
    };

    // console.log("Video Type:", videoType);
    // Handle YouTube URL change with validation
    const handleYoutubeUrlChange = (value, field, setError) => {
        // Update the field value regardless of validation
        props.updateAttributes({ [field]: value });

        // Validate and set error message
        if (value && !validateYoutubeUrl(value)) {
            setError(__("Please enter a valid YouTube URL", "wp-before-after-image-slider"));
        } else {
            setError("");
        }
    };

    if (isTripleEnabled) {
        tabTitle = __("Comparable", "wp-before-after-image-slider");
    }

    const SliderTypeField = (
        <div className={"field-control field-inline"}>
            <span className="field-control__label">{__("Slider Type", "wp-before-after-image-slider")}</span>
            <div className={"fields-wrapper inline"}>
                <SelectControl
                    className="large-select"
                    value={props.attributes.slider_type}
                    onChange={(value) => props.updateAttributes({ slider_type: value })}
                    options={[
                        { label: "Two Image", value: "default" },
                        {
                            label: "Three Image " + (!props.proSettings.is_pro_active ? "(Pro)" : ""),
                            value: "triple",
                            disabled: !props.proSettings.is_pro_active,
                        },
                        {
                            label: "Video " + (!props.proSettings.is_pro_active ? "(Pro)" : ""),
                            value: "video",
                            disabled: !props.proSettings.is_pro_active,
                        },
                        {
                            label: "Retouch " + (!props.proSettings.is_pro_active ? "(Pro)" : ""),
                            value: "retouch",
                            disabled: !props.proSettings.is_pro_active,
                        },
                    ]}
                />
                {/* {isTriple || isVideo ? <img src={coreAssets.icons.premium} alt="Premium Icon" /> : null} */}
                {!props.proSettings.is_pro_active && (
                    <a
                        href="https://codecanel.com/wp-before-after-image-slider/#product-pricing"
                        target="_blank"
                        style={{ marginTop: "6px" }}
                        title="Buy Pro"
                    >
                        <img src={coreAssets.icons.premium} alt="Premium Icon" />
                    </a>
                )}
            </div>
            {/* {!props.proSettings.is_pro_active && (
                <div className="pro-feature-notice" style={{ marginTop: "8px", fontSize: "12px", color: "#d63638" }}>
                    {__("Triple Image and Video sliders require a Pro license.", "wp-before-after-image-slider")}

                    <a
                        href="https://codecanel.com/wp-before-after-image-slider/"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ marginLeft: "5px", textDecoration: "underline" }}
                    >
                        {__("Get Pro", "wp-before-after-image-slider")}
                    </a>
                </div>
            )} */}
        </div>
    );

    const ImageSizeField = (
        <div className={"field-control field-inline"}>
            <span className="field-control__label">{__("Image Size", "wp-before-after-image-slider")}</span>
            <div className={"fields-wrapper inline"}>
                <SelectControl
                    className="large-select"
                    value={props.attributes.image_size || "full"}
                    onChange={(value) => props.updateAttributes({ image_size: value })}
                    options={[].map.call(window.COCA_MEDIA_SIZES, (size) => ({ label: ucFirst(size), value: size }))}
                />
            </div>
        </div>
    );
    const ImageUploadField = (options) => {
        // Create a memoized onChange handler for this specific field
        const handleImageChange = (attachment) => {
            // Only update the specific image attribute
            const update = {};
            update[options.attribute] = attachment;
            props.updateAttributes(update);
        };

        return (
            <div className={"field-control field-col__row field-control__start"}>
                <span className="field-control__label">{options.labelText}</span>
                <div className={"fields-wrapper inline"}>
                    <MediaUploader value={options.value} onChange={handleImageChange} />
                </div>
            </div>
        );
    };
    const ImageLabelField = (options) => (
        <div className={"field-control field-inline"}>
            <span className="field-control__label">{options.labelText}</span>
            <div className={"fields-wrapper inline"}>
                <TextControl
                    className="large-input"
                    value={options.value ? options.value : ""}
                    onChange={(value) => props.updateAttributes({ [options.attribute]: value })}
                />
            </div>
        </div>
    );

    const ImageCaptionField = (options) => (
        <div className={"field-control field-inline"}>
            <span className="field-control__label">{options.labelText}</span>
            <div className={"fields-wrapper inline"}>
                <TextControl
                    className="large-input"
                    value={options.value ? options.value : ""}
                    onChange={(value) => props.updateAttributes({ [options.attribute]: value })}
                    placeholder={options.placeholder}
                />
            </div>
        </div>
    );
    return (
        <PanelBody
            title={tabTitle}
            initialOpen={false}
            opened={props.attributes.before_after_panel_open}
            onToggle={() =>
                props.updateAttributes({ before_after_panel_open: !props.attributes.before_after_panel_open })
            }
        >
            <PanelRow>
                {SliderTypeField}
                {!isTriple && !isVideo && !isRetouch && (
                    <div className="field-control__grid">
                        {ImageUploadField({
                            labelText: __("Before Image ", "wp-before-after-image-slider"),
                            attribute: "before_image",
                            value: props.attributes.before_image,
                        })}
                        {ImageUploadField({
                            labelText: __("After Image", "wp-before-after-image-slider"),
                            attribute: "after_image",
                            value: props.attributes.after_image,
                        })}
                    </div>
                )}
                {isVideo && (
                    <>
                        {/* Video Type Selection */}
                        <div className={"field-control field-inline border-bottom-none"}>
                            <span className="field-control__label">
                                {__("Video Source", "wp-before-after-image-slider")}
                            </span>
                            <div className={"fields-wrapper inline"}>
                                <RadioControl
                                    selected={videoType}
                                    options={[
                                        { label: "YouTube Link", value: "youtube" },
                                        { label: "Upload Video", value: "upload" },
                                    ]}
                                    onChange={(value) => {
                                        // Update both the local state and the parent component's state
                                        setVideoType(value);

                                        // Create an update object with all the necessary attributes
                                        const updates = {
                                            video_source_type: value,
                                        };

                                        // If switching to YouTube and no YouTube URLs are set, initialize with empty strings

                                        // Update all attributes at once
                                        props.updateAttributes(updates);
                                    }}
                                    className="radio-control-inline"
                                    style={{
                                        "--wp-components-radio-control-options-display": "inline-block",
                                        "--wp-components-radio-control-option-margin-bottom": "0",
                                    }}
                                />
                            </div>
                        </div>

                        {videoType === "youtube" && (
                            <>
                                <div className={"field-control field-inline youtube-wrap-before field-control__start"}>
                                    <span className="field-control__label">
                                        {__("Before Youtube Link", "wp-before-after-image-slider")}
                                    </span>
                                    <div className={"fields-wrapper inline field-col"}>
                                        <TextControl
                                            value={props.attributes.video_before_youtube || ""}
                                            onChange={(value) =>
                                                handleYoutubeUrlChange(
                                                    value,
                                                    "video_before_youtube",
                                                    setBeforeYoutubeError,
                                                )
                                            }
                                            placeholder={__("Paste YouTube Watch URL", "wp-before-after-image-slider")}
                                        />
                                        {beforeYoutubeError && (
                                            <div
                                                className="field-error"
                                                style={{ color: "red", fontSize: "12px", marginTop: "5px" }}
                                            >
                                                {beforeYoutubeError}
                                            </div>
                                        )}

                                        <div className="fields-wrapper">
                                            <div>
                                                <span className="field-control__label" style={{ fontWeight: "400" }}>
                                                    {__("Start Time (in sec)", "wp-before-after-image-slider")}
                                                </span>
                                                <NumberControl
                                                    isShiftStepEnabled={true}
                                                    onChange={(value) =>
                                                        props.updateAttributes({ video_before_start_time: value })
                                                    }
                                                    shiftStep={1}
                                                    value={props.attributes.video_before_start_time || 0}
                                                    min={0}
                                                />
                                            </div>
                                            <div>
                                                <span className="field-control__label" style={{ fontWeight: "400" }}>
                                                    {__("End Time (in sec)", "wp-before-after-image-slider")}
                                                </span>
                                                <NumberControl
                                                    isShiftStepEnabled={true}
                                                    onChange={(value) =>
                                                        props.updateAttributes({ video_before_end_time: value })
                                                    }
                                                    shiftStep={1}
                                                    value={props.attributes.video_before_end_time || 0}
                                                    min={0}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className={"field-control field-inline youtube-wrap-after field-control__start"}>
                                    <span className="field-control__label">
                                        {__("After Youtube Link", "wp-before-after-image-slider")}
                                    </span>
                                    <div className={"fields-wrapper inline field-col"}>
                                        <TextControl
                                            value={props.attributes.video_after_youtube || ""}
                                            onChange={(value) =>
                                                handleYoutubeUrlChange(
                                                    value,
                                                    "video_after_youtube",
                                                    setAfterYoutubeError,
                                                )
                                            }
                                            placeholder={__("Paste YouTube Watch URL", "wp-before-after-image-slider")}
                                        />
                                        {afterYoutubeError && (
                                            <div
                                                className="field-error"
                                                style={{ color: "red", fontSize: "12px", marginTop: "5px" }}
                                            >
                                                {afterYoutubeError}
                                            </div>
                                        )}

                                        <div className="fields-wrapper">
                                            <div>
                                                <span className="field-control__label" style={{ fontWeight: "400" }}>
                                                    {__("Start Time (in sec)", "wp-before-after-image-slider")}
                                                </span>
                                                <NumberControl
                                                    isShiftStepEnabled={true}
                                                    onChange={(value) =>
                                                        props.updateAttributes({ video_after_start_time: value })
                                                    }
                                                    shiftStep={1}
                                                    value={props.attributes.video_after_start_time || 0}
                                                    min={0}
                                                />
                                            </div>
                                            <div>
                                                <span className="field-control__label" style={{ fontWeight: "400" }}>
                                                    {__("End Time (in sec)", "wp-before-after-image-slider")}
                                                </span>
                                                <NumberControl
                                                    isShiftStepEnabled={true}
                                                    onChange={(value) =>
                                                        props.updateAttributes({ video_after_end_time: value })
                                                    }
                                                    shiftStep={1}
                                                    value={props.attributes.video_after_end_time || 0}
                                                    min={0}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {videoType === "upload" && (
                            <>
                                <div className={"field-control field-inline field-control__start"}>
                                    <span className="field-control__label">
                                        {__("Before Video", "wp-before-after-image-slider")}
                                    </span>
                                    <div className={"fields-wrapper inline field-col"}>
                                        <MediaUploader
                                            value={props.attributes.video_before_file}
                                            onChange={(attachment) =>
                                                props.updateAttributes({ video_before_file: attachment })
                                            }
                                            mediaType="video"
                                        />
                                        <div className="fields-wrapper">
                                            <div>
                                                <span className="field-control__label" style={{ fontWeight: "400" }}>
                                                    {__("Start Time (in sec)", "wp-before-after-image-slider")}
                                                </span>
                                                <NumberControl
                                                    isShiftStepEnabled={true}
                                                    onChange={(value) =>
                                                        props.updateAttributes({
                                                            video_before_upload_start_time: value,
                                                        })
                                                    }
                                                    shiftStep={1}
                                                    value={props.attributes.video_before_upload_start_time || 0}
                                                    min={0}
                                                />
                                            </div>
                                            <div>
                                                <span className="field-control__label" style={{ fontWeight: "400" }}>
                                                    {__("End Time (in sec)", "wp-before-after-image-slider")}
                                                </span>
                                                <NumberControl
                                                    isShiftStepEnabled={true}
                                                    onChange={(value) =>
                                                        props.updateAttributes({ video_before_upload_end_time: value })
                                                    }
                                                    shiftStep={1}
                                                    value={props.attributes.video_before_upload_end_time || 0}
                                                    min={0}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className={"field-control field-inline field-control__start"}>
                                    <span className="field-control__label">
                                        {__("After Video", "wp-before-after-image-slider")}
                                    </span>
                                    <div className={"fields-wrapper inline field-col"}>
                                        <MediaUploader
                                            value={props.attributes.video_after_file}
                                            onChange={(attachment) =>
                                                props.updateAttributes({ video_after_file: attachment })
                                            }
                                            mediaType="video"
                                        />

                                        <div className="fields-wrapper">
                                            <div>
                                                <span className="field-control__label" style={{ fontWeight: "400" }}>
                                                    {__("Start Time (in sec)", "wp-before-after-image-slider")}
                                                </span>
                                                <NumberControl
                                                    isShiftStepEnabled={true}
                                                    onChange={(value) =>
                                                        props.updateAttributes({ video_after_upload_start_time: value })
                                                    }
                                                    shiftStep={1}
                                                    value={props.attributes.video_after_upload_start_time || 0}
                                                    min={0}
                                                />
                                            </div>
                                            <div>
                                                <span className="field-control__label" style={{ fontWeight: "400" }}>
                                                    {__("End Time (in sec)", "wp-before-after-image-slider")}
                                                </span>
                                                <NumberControl
                                                    isShiftStepEnabled={true}
                                                    onChange={(value) =>
                                                        props.updateAttributes({ video_after_upload_end_time: value })
                                                    }
                                                    shiftStep={1}
                                                    value={props.attributes.video_after_upload_end_time || 0}
                                                    min={0}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                )}
                {isRetouch && (
                    <>
                        <div className={"field-control field-col field-control__start"}>
                            <span className="field-control__label">{__("Retouch Image", "wp-before-after-image-slider")}</span>
                            <div className={"fields-wrapper inline"}>
                                <MediaUploader
                                    value={props.attributes.retouch_image}
                                    onChange={(attachment) => props.updateAttributes({ retouch_image: attachment })}
                                />
                            </div>
                        </div>
                        <div className={"field-control__grid"} style={{ gridTemplateColumns: "1fr 1fr" }}>
                            <div className={"field-control field-inline"}>
                                <span className="field-control__label">{__("CSS Filter", "wp-before-after-image-slider")}</span>
                                <div className={"fields-wrapper inline"}>
                                    <SelectControl
                                        className="large-select"
                                        value={props.attributes.retouch_filter || "grayscale"}
                                        onChange={(value) => props.updateAttributes({ retouch_filter: value })}
                                        options={[
                                            { label: __("Grayscale", "wp-before-after-image-slider"), value: "grayscale" },
                                            { label: __("Blur", "wp-before-after-image-slider"), value: "blur" },
                                            { label: __("Sepia", "wp-before-after-image-slider"), value: "sepia" },
                                            { label: __("Saturation", "wp-before-after-image-slider"), value: "saturate" },
                                            { label: __("Sharpen", "wp-before-after-image-slider"), value: "sharpen" },
                                        ]}
                                    />
                                </div>
                            </div>
                            <div className={"field-control field-inline"}>
                                <span className="field-control__label">{__("Apply Filter On", "wp-before-after-image-slider")}</span>
                                <div className={"fields-wrapper inline"}>
                                    <RadioControl
                                        selected={props.attributes.retouch_apply_on || "after"}
                                        onChange={(value) => props.updateAttributes({ retouch_apply_on: value })}
                                        options={[
                                            { label: __("Before Side", "wp-before-after-image-slider"), value: "before" },
                                            { label: __("After Side", "wp-before-after-image-slider"), value: "after" },
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={"field-control field-inline"}>
                            <span className="field-control__label">{__("Filter Intensity", "wp-before-after-image-slider")}</span>
                            <div className={"fields-wrapper inline"}>
                                <RangeControl
                                    initialPosition={100}
                                    trackColor="#4A25E1"
                                    railColor="#603CFF7A"
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={Number(props.attributes.retouch_intensity ?? 100)}
                                    onChange={(value) => { if (value !== undefined) props.updateAttributes({ retouch_intensity: value }); }}
                                />
                            </div>
                        </div>
                        {ImageSizeField}
                    </>
                )}

                {isTriple && (
                    <div className="field-control__grid">
                        {ImageUploadField({
                            labelText: __("Before Image ", "wp-before-after-image-slider"),
                            attribute: "before_image",
                            value: props.attributes.before_image,
                        })}
                        {ImageUploadField({
                            labelText: __("Middle Image", "wp-before-after-image-slider"),
                            attribute: "middle_image",
                            value: props.attributes.middle_image,
                        })}
                        {ImageUploadField({
                            labelText: __("After Image", "wp-before-after-image-slider"),
                            attribute: "after_image",
                            value: props.attributes.after_image,
                        })}
                    </div>
                )}

                <div className={"field-control field-gap"}></div>

                {!isTriple && !isVideo && !isRetouch && (
                    <div className="field-control__grid__label_field">
                        {ImageLabelField({
                            labelText: __("Before Image Label", "wp-before-after-image-slider"),
                            attribute: "before_image_label",
                            value: props.attributes.before_image_label,
                        })}
                        {ImageLabelField({
                            labelText: __("After Image Label", "wp-before-after-image-slider"),
                            attribute: "after_image_label",
                            value: props.attributes.after_image_label,
                        })}
                        {ImageCaptionField({
                            labelText: __("Before Image Caption", "wp-before-after-image-slider"),
                            attribute: "before_image_caption",
                            value: props.attributes.before_image_caption,
                            placeholder: __("Type before image caption", "wp-before-after-image-slider"),
                        })}
                        {ImageCaptionField({
                            labelText: __("After Image Caption", "wp-before-after-image-slider"),
                            attribute: "after_image_caption",
                            value: props.attributes.after_image_caption,
                            placeholder: __("Type after image caption", "wp-before-after-image-slider"),

                        })}
                    </div>
                )}
                {isRetouch && (
                    <div className="field-control__grid__label_field">
                        {ImageLabelField({
                            labelText: __("Before Label", "wp-before-after-image-slider"),
                            attribute: "before_image_label",
                            value: props.attributes.before_image_label,
                        })}
                        {ImageLabelField({
                            labelText: __("After Label", "wp-before-after-image-slider"),
                            attribute: "after_image_label",
                            value: props.attributes.after_image_label,
                        })}
                    </div>
                )}
                {isVideo && (
                    <div className="field-control__grid">
                        {ImageLabelField({
                            labelText: __("Before Video Label", "wp-before-after-image-slider"),
                            attribute: "video_before_label",
                            value: props.attributes.video_before_label,
                        })}
                        {ImageLabelField({
                            labelText: __("After Video Label", "wp-before-after-image-slider"),
                            attribute: "video_after_label",
                            value: props.attributes.video_after_label,
                        })}
                    </div>
                )}
                {isTriple && (
                    <div className="field-control__grid__label">
                        {ImageLabelField({
                            labelText: __("Before Image Label", "wp-before-after-image-slider"),
                            attribute: "before_image_label",
                            value: props.attributes.before_image_label,
                        })}
                        {ImageLabelField({
                            labelText: __("Middle Image Label", "wp-before-after-image-slider"),
                            attribute: "middle_image_label",
                            value: props.attributes.middle_image_label,
                        })}
                        {ImageLabelField({
                            labelText: __("After Image Label", "wp-before-after-image-slider"),
                            attribute: "after_image_label",
                            value: props.attributes.after_image_label,
                        })}
                        {ImageCaptionField({
                            labelText: __("Before Image Caption", "wp-before-after-image-slider"),
                            attribute: "before_image_caption",
                            value: props.attributes.before_image_caption,
                            placeholder: __("Type before image caption", "wp-before-after-image-slider"),

                        })}
                        {ImageCaptionField({
                            labelText: __("Middle Image Caption", "wp-before-after-image-slider"),
                            attribute: "middle_image_caption",
                            value: props.attributes.middle_image_caption,
                            placeholder: __("Type middle image caption", "wp-before-after-image-slider"),

                        })}
                        {ImageCaptionField({
                            labelText: __("After Image Caption", "wp-before-after-image-slider"),
                            attribute: "after_image_caption",
                            value: props.attributes.after_image_caption,
                            placeholder: __("Type after image caption", "wp-before-after-image-slider"),

                        })}
                        {ImageSizeField}
                    </div>
                )}

                {!isVideo && !isTriple && !isRetouch && ImageSizeField}
                <IndicatorImageContentTab {...props} />
            </PanelRow>
        </PanelBody>
    );
};
